<div class="tab-content api-key-management">
  <p class="double-margin-bottom">
    Manage API keys for programmatic access to your Arpio account. API keys can be assigned roles to control their
    permissions.
  </p>
  <div class="flex justify-between items-center margin-top">
    <div class="flex items-center margin-bottom">
      @if (canManageApiKeys$ | async) {
        <button class="arpio-button small-button" (click)="openNewApiKeyModal()" data-cy="create-api-key-button">
          <svg lucidePlus class="text-sm"></svg>Create New API Key
        </button>
      }
    </div>
    <div>
      <div class="filter-container">
        <input
          pInputText
          class="input-small"
          #filterInput
          type="text"
          (input)="filterGlobal($event)"
          placeholder="Search..."
        />
        @if (!isFilterApplied && !loadingFilterInput) {
          <svg lucideFunnel class="filter-icon fade-icon"></svg>
        }
        @if (isFilterApplied && !loadingFilterInput) {
          <svg lucideX class="filter-clear-icon" (click)="clearInput()"></svg>
        }
        @if (loadingFilterInput) {
          <svg lucideLoaderCircle class="filter-spinner lucide-spin"></svg>
        }
      </div>
    </div>
  </div>

  <!-- Unauthorized State -->
  @if ((canViewApiKeys$ | async) === false) {
    <div class="unauthorized-container" data-cy="unauthorized-message">
      <div class="unauthorized-content">
        <h3>Access restricted</h3>
        <p>{{ unauthorizedMessage$ | async }}</p>
      </div>
    </div>
  }

  <!-- Authorized Content -->
  @if ((canViewApiKeys$ | async) === true && apiKeys != null) {
    <div class="table-container">
      <!-- Empty State -->
      @if (apiKeys.length === 0) {
        <div class="empty-state-container">
          <div class="empty-state-content">
            <h3>No API keys have been created yet</h3>
            <p>API keys allow programmatic access to your Arpio account. Create your first API key to get started.</p>
            @if (canManageApiKeys$ | async) {
              <button class="arpio-button" (click)="openNewApiKeyModal()">
                <svg lucidePlus></svg>
                Create Your First API Key
              </button>
            }
          </div>
        </div>
      }
      <!-- Table Section -->
      @if (apiKeys.length > 0) {
        <div class="table-section main-table">
          <p-table
            #dt1
            [value]="apiKeys"
            [globalFilterFields]="globalFilterFields"
            data-cy="api-key-table"
            dataKey="id"
            [scrollable]="true"
            scrollHeight="60vh"
            [tableStyle]="{ 'min-width': '30rem' }"
            class="p-datatable-sm arp-prime-datatable"
            [rowHover]="true"
            [totalRecords]="apiKeys?.length ?? 0"
            [paginator]="false"
            [rowTrackBy]="apiKeyTrackBy"
            selectionMode="single"
            [(selection)]="selectedTableItem"
            (onRowSelect)="onRowSelect($event.data)"
            [customSort]="true"
            (sortFunction)="customSort($event)"
          >
            <ng-template #header>
              <tr>
                <th pSortableColumn="name" class="strong-text name-column">
                  Name <p-sort-icon field="name"></p-sort-icon>
                </th>
                <th pSortableColumn="enabled" class="strong-text enabled-column">
                  Status <p-sort-icon field="enabled"></p-sort-icon>
                </th>
                <th pSortableColumn="roleCount" class="strong-text role-count-column">
                  Roles <p-sort-icon field="roleCount"></p-sort-icon>
                </th>
                <th pSortableColumn="createdAt" class="strong-text created-at-column">
                  Created <p-sort-icon field="createdAt"></p-sort-icon>
                </th>
              </tr>
            </ng-template>
            <ng-template #body let-apiKey let-index="rowIndex">
              <tr
                [pSelectableRow]="apiKey"
                class="cursor-pointer"
                [ngClass]="{
                  'selected-row': showSidePanel && selectedApiKey && selectedApiKey.id === apiKey.id,
                  'refreshing-row': isApiKeyRefreshing(apiKey),
                }"
              >
                <td class="name-column" style="position: relative">
                  <div class="apikey-info">
                    <div class="rbac-table-display-container">
                      @if (isApiKeyRefreshing(apiKey)) {
                        <svg lucideLoaderCircle class="half-margin-right lucide-spin"></svg>
                      }
                      <span>{{ apiKey.name }}</span>
                    </div>
                  </div>
                </td>
                <td class="enabled-column">
                  <span>{{ apiKey.enabled ? 'Enabled' : 'Disabled' }}</span>
                </td>
                <td class="role-count-column">
                  <div class="flex items-center">
                    @if (isApiKeyRefreshing(apiKey)) {
                      <svg lucideLoaderCircle class="half-margin-right lucide-spin"></svg>
                    }
                    @if (apiKey.roles.length !== 0) {
                      <div class="roles-list">
                        @for (role of apiKey.roles | slice: 0 : 2; track role) {
                          <p-tag
                            severity="info"
                            class="role-tag clickable-tag fit-width"
                            (click)="navigateToRole(role.name)"
                            (keydown.enter)="navigateToRole(role.name)"
                            (keydown.space)="navigateToRole(role.name)"
                            tabindex="0"
                            value="{{ role.name }}"
                          >
                          </p-tag>
                        }
                        @if (apiKey.roles.length > 2) {
                          <span
                            class="more-count"
                            [pTooltip]="getAdditionalRolesTooltip(apiKey)"
                            tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                            tooltipPosition="top"
                            [autoHide]="false"
                            [hideDelay]="200"
                            [escape]="false"
                            tooltipEvent="hover"
                          >
                            +{{ apiKey.roles.length - 2 }} more
                          </span>
                        }
                      </div>
                    } @else {
                      <span class="no-data-text">None</span>
                    }
                  </div>
                </td>
                <td class="created-at-column">
                  <div class="rbac-table-display-container">
                    <span
                      [pTooltip]="getLastModifiedTooltip(apiKey.createdAt)"
                      tooltipStyleClass="arp-tooltip"
                      tooltipPosition="top"
                      [autoHide]="false"
                      [hideDelay]="200"
                      tooltipEvent="hover"
                      >{{ getLastModifiedDisplay(apiKey.createdAt) }}</span
                    >
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="4" class="empty-state-cell text-center">
                  <div class="empty-state-content">
                    <p class="empty-state-message">No results found.</p>
                    <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #summary>
              <div class="flex align-items-center justify-content-between">
                <span
                  >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of {{ dt1.value?.length }} API
                  keys</span
                >
              </div>
            </ng-template>
          </p-table>
        </div>
      }
      <!-- Side Panel -->
      <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel>
        <div class="side-panel-header">
          <div class="flex items-center">
            <button class="close-btn" (click)="closeSidePanel()">
              <svg lucideChevronRight></svg>
            </button>
            <h3>API Key Details</h3>
          </div>
          <div>
            @if (canManageApiKeys$ | async) {
              <button class="edit-button" (click)="openEditApiKeyModal()">
                <svg lucideSquarePen></svg>
              </button>
            }
          </div>
        </div>
        <div class="side-panel-content">
          <!-- Basic Information Section -->
          <div class="detail-section">
            <div class="detail-item">
              <strong>Name:</strong>
              @if (showSidePanel && selectedApiKey) {
                <span>{{ selectedApiKey.name }}</span>
              }
              @if (!showSidePanel || !selectedApiKey) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <strong>Status:</strong>
              @if (showSidePanel && selectedApiKey) {
                <span>
                  <p-tag
                    [severity]="selectedApiKey.enabled ? 'success' : 'danger'"
                    [value]="selectedApiKey.enabled ? 'Enabled' : 'Disabled'"
                  ></p-tag>
                </span>
              }
              @if (!showSidePanel || !selectedApiKey) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <strong>API Key ID:</strong>
              @if (showSidePanel && selectedApiKey) {
                <span class="monospace">{{ selectedApiKey.id }}</span>
              }
              @if (!showSidePanel || !selectedApiKey) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Roles Section -->
          <div class="detail-section">
            @if (showSidePanel && selectedApiKey) {
              <h4>Roles ({{ selectedApiKey.roles.length }})</h4>
            }
            @if (!showSidePanel || !selectedApiKey) {
              <h4>Roles</h4>
            }
            <div class="roles-list detail-item">
              @if (showSidePanel && selectedApiKey) {
                @for (role of selectedApiKey.roles; track role) {
                  <p-tag
                    [value]="role.name"
                    severity="info"
                    class="role-tag clickable-tag"
                    (click)="navigateToRole(role.name)"
                    (keydown.enter)="navigateToRole(role.name)"
                    (keydown.space)="navigateToRole(role.name)"
                    tabindex="0"
                  ></p-tag>
                }
                @if (selectedApiKey.roles.length === 0) {
                  <span class="no-data-text">No roles assigned</span>
                }
              }
              @if (!showSidePanel || !selectedApiKey) {
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                <div class="tag-skeleton"></div>
              }
            </div>
          </div>
          <!-- Additional Information Section -->
          <div class="detail-section">
            <h4>Additional Information</h4>
            <div class="detail-item">
              <strong>Created:</strong>
              @if (showSidePanel && selectedApiKey) {
                <span>{{ selectedApiKey.createdAt | date: 'medium' }}</span>
              }
              @if (!showSidePanel || !selectedApiKey) {
                <div class="detail-skeleton"></div>
              }
            </div>
            @if (selectedApiKey?.lastUpdatedAt) {
              <div class="detail-item">
                <strong>Last Modified:</strong>
                @if (showSidePanel && selectedApiKey) {
                  <span>{{ selectedApiKey.lastUpdatedAt | date: 'medium' }}</span>
                }
                @if (!showSidePanel || !selectedApiKey) {
                  <div class="detail-skeleton"></div>
                }
              </div>
            }
          </div>
          <!-- Action Buttons Section -->
          @if (showSidePanel && selectedApiKey && (canManageApiKeys$ | async)) {
            <div class="detail-section action-buttons">
              <h4>Actions</h4>
              <div class="margin-auto text-center margin-top">
                <a class="delete-role-button text-center" (click)="deleteApiKey()">Delete API Key...</a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else {
    <svg lucideLoaderCircle class="lucide-spin"></svg> Loading API keys...
  }

  <app-new-api-key-modal #newApiKeyModal (apiKeyCreated)="onApiKeyCreated()"></app-new-api-key-modal>
  <app-edit-api-key-modal #editApiKeyModal (apiKeyUpdated)="onApiKeyUpdated($event)"></app-edit-api-key-modal>

  <!-- Delete API Key Confirmation Dialog -->
  <modal-dialog #deleteApiKeyDialog [dialogTemplate]="confirmDeleteApiKeyDialog"></modal-dialog>
  <ng-template #confirmDeleteApiKeyDialog>
    <confirm-action-dialog
      name="&quot;{{ apiKeyToDelete?.name }}&quot;"
      action="Delete"
      dialogId="delete-api-key-confirm"
      (onCancel)="cancelDeleteApiKey()"
      (onAction)="confirmDeleteApiKey()"
    >
      <div class="error margin-top">
        Deleting this API key will revoke its access immediately. This action cannot be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
